/**
 * Location context (multi-location). A company has multiple lab locations; the user
 * switches between them. The current selection is persisted to localStorage so it
 * survives reloads; on first load (or if the stored id no longer exists) we fall back
 * to the org's `is_default` location.
 *
 * Every formats / configs / upload call should pass `location_id` = the current
 * location (see `useCurrentLocation()`).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useLocations } from "../api/locations";
import type { Location } from "../api/types";

const LOCATION_STORAGE_KEY = "eddkit.location";

function readStoredId(): string | null {
  try {
    return localStorage.getItem(LOCATION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredId(id: string | null): void {
  try {
    if (id) localStorage.setItem(LOCATION_STORAGE_KEY, id);
    else localStorage.removeItem(LOCATION_STORAGE_KEY);
  } catch {
    /* localStorage unavailable (private mode) — selection just won't persist */
  }
}

interface LocationState {
  /** The org's locations (from GET /locations). */
  locations: Location[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** The resolved current location, or null until locations load. */
  current: Location | null;
  /** The current location id (for passing to formats/configs/upload calls). */
  currentId: string | null;
  /** Switch to a different location by id. */
  setCurrentId: (id: string) => void;
}

const LocationContext = createContext<LocationState | null>(null);

export function LocationProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, error } = useLocations();
  const locations = useMemo(() => data ?? [], [data]);

  const [selectedId, setSelectedId] = useState<string | null>(() => readStoredId());

  // Resolve the effective current location: the stored choice if it still exists,
  // otherwise the org default, otherwise the first location.
  const current = useMemo<Location | null>(() => {
    if (locations.length === 0) return null;
    const stored = selectedId
      ? locations.find((l) => l.id === selectedId)
      : undefined;
    if (stored) return stored;
    return locations.find((l) => l.is_default) ?? locations[0];
  }, [locations, selectedId]);

  // Once resolved, keep the persisted id in sync (heals a stale/empty stored id).
  useEffect(() => {
    if (current && current.id !== selectedId) {
      setSelectedId(current.id);
      writeStoredId(current.id);
    }
  }, [current, selectedId]);

  const setCurrentId = useCallback((id: string) => {
    setSelectedId(id);
    writeStoredId(id);
  }, []);

  const value = useMemo<LocationState>(
    () => ({
      locations,
      isLoading,
      isError,
      error,
      current,
      currentId: current?.id ?? null,
      setCurrentId,
    }),
    [locations, isLoading, isError, error, current, setCurrentId],
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useCurrentLocation(): LocationState {
  const ctx = useContext(LocationContext);
  if (!ctx) {
    throw new Error("useCurrentLocation must be used within a LocationProvider");
  }
  return ctx;
}
